import { Link } from 'react-router-dom'
import moment from 'moment'
import { PencilIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'


// Derive status if backend didn’t set it explicitly
const deriveStatus = inv => {
	if (inv?.status) return inv.status
	const paid = inv?.paidDate || inv?.paid || inv?.paymentDate
	const term = inv?.paymentTerm
	if (paid) return 'paid'
	if (term && moment().isAfter(moment(term))) return 'overdue'
	return 'issued'
}

const statusBadgeClass = s => {
	switch ((s || '').toLowerCase()) {
		case 'paid':
			return 'bg-green-100 text-green-800'
		case 'overdue':
			return 'bg-red-100 text-red-800'
		default:
			return 'bg-gray-100 text-gray-800'
	}
}

export default function KorektyTableRow({ invoice, isSelected, onSelectInvoice, systemSettings, onAssignPlacowka }) {
	const { t } = useTranslation()
	const status = deriveStatus(invoice)
	const currencySymbol = systemSettings?.companyCurrencySymbol || 'PLN'

	const formatDate = d => (d ? moment(d).format('YYYY-MM-DD') : t('korekty.list.table.na'))
	const formatCurrency = amount =>
		amount && amount.$numberDecimal != null ? `${currencySymbol} ${parseFloat(amount.$numberDecimal).toFixed(2)}` : `${currencySymbol} 0.00`
	const statusLabel = t(`korekty.list.statusFilter.options.${status}`, { defaultValue: status })

	const customerName = (invoice.customerName || invoice.customerId?.name || '').trim()
	const customerId = (invoice.customerId?._id || invoice.customerId || '').toString().trim()
	const placowkaLabel = customerName || t('korekty.list.table.assignPlacowka')
	const hasAssignedPlacowka = Boolean(customerName && customerId)
	const hasInfaktInvoice = Boolean(invoice.infaktInvoiceUuid || invoice.infakt_invoice_uuid)

	return (
		<tr className="hover:bg-gray-100">
			<td className="py-2 px-4 border-b border-gray-300 text-left w-12">
				<input
					type="checkbox"
					checked={isSelected}
					onChange={e => onSelectInvoice(invoice._id, e.target.checked)}
					className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
				/>
			</td>

			{/* Invoice # */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				<div className="flex items-center gap-2">
					<svg className="w-4 h-4 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
						/>
					</svg>
					<Link
						to={`/ksiegowosc/korekty/details/${invoice._id}`}
						className="font-mono text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200">
						{invoice.number || t('korekty.list.table.na')}
					</Link>
					{hasInfaktInvoice && (
						<img
							src="/infakt-logo.svg"
							alt="inFakt"
							title="inFakt"
							className="h-4 w-4 shrink-0"
						/>
					)}
				</div>
			</td>

			{/* Firma */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				<span>{invoice.companyName || t('korekty.list.table.na')}</span>
			</td>

			{/* Corrected invoice # */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				<span className="font-mono text-sm">
					{invoice.correctedInvoiceNumber || t('korekty.list.table.na')}
				</span>
			</td>

			{/* Nazwa Infakt */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				<span>{invoice.infakt_client_company_name || t('korekty.list.table.na')}</span>
			</td>

			{/* Placowka */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				{hasAssignedPlacowka ? (
					<div className="flex items-center gap-2">
						<Link
							to={`/klienci/placowki/details/${customerId}`}
							className="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200">
							{placowkaLabel}
						</Link>
						<button
							type="button"
							onClick={() => onAssignPlacowka?.(invoice._id)}
							className="rounded-md border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
							{t('korekty.list.table.changePlacowka')}
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => onAssignPlacowka?.(invoice._id)}
						className="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200">
						{placowkaLabel}
					</button>
				)}
			</td>

			{/* Inv Date */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-black">
					{formatDate(invoice.invoiceDate)}
				</span>
			</td>

			{/* Status (sortable via header in parent) */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				<span
					className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
						status
					)}`}>
					{statusLabel}
				</span>
			</td>

			{/* Total */}
			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap">
				<div className="flex justify-start">
					<span className="font-semibold text-green-600">{formatCurrency(invoice.total)}</span>
				</div>
			</td>

			<td className="py-2 px-4 border-b border-gray-300 whitespace-nowrap text-right">
				{!hasInfaktInvoice && (
					<Link
						to={`/ksiegowosc/korekty/edit/${invoice._id}`}
						className="inline-flex items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
					>
						<PencilIcon className="h-4 w-4" />
						<span>{t('korekty.list.table.edit')}</span>
					</Link>
				)}
			</td>
		</tr>
	)
}
